// Shared probe→auth derivation for the Add MCP modal and the Edit MCP panel.
// One source of truth for how a probe result + optional static Client ID maps
// onto the persisted auth_type — keep in lockstep with the legacy /mcp/add
// wizard until it migrates onto these helpers too.

import type { MCPProbeResult } from "@/services/mcp-service";
import type { AuthConfig } from "@/services/auth-config-service";

export type McpConnectionMode = "auto" | "direct";

export type DerivedAuthType =
  | "none"
  | "oauth2"
  | "oauth2_cimd"
  | "oauth2_client_credentials";

// Auth types that go through executor /v1/mcp/connect (popup OR no-op).
// "none" just persists the config (custom headers carry any auth).
export const REQUIRES_CONNECT_CALL: ReadonlySet<DerivedAuthType> = new Set([
  "oauth2",
  "oauth2_cimd",
  "oauth2_client_credentials",
]);

// Minimal typed view of the RFC 8414 Authorization Server Metadata fields the
// wizard reads. probe.as_metadata is `unknown`; cast through this shape.
export interface AsMetadataShape {
  authorization_endpoint?: string;
  token_endpoint?: string;
  registration_endpoint?: string;
}

// Minimal typed view of the RFC 9728 Protected Resource Metadata fields the
// wizard reads — advertised scopes forwarded into the authorize request.
export interface PrmShape {
  scopes_supported?: string[];
}

export function isValidHttpUrl(value: string): boolean {
  try {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

// OAuth authorization/token endpoints still require HTTPS.
export function isValidHttpsUrl(value: string): boolean {
  if (!value) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

// Servers that need STATIC OAuth credentials: OAuth is required, the hint is
// plain oauth2 and the AS offers no Dynamic Client Registration endpoint.
// The UI reveals the credentials section for exactly this case (design d02/d03).
export function probeNeedsCredentials(probe: MCPProbeResult): boolean {
  const asMeta = probe.as_metadata as AsMetadataShape | undefined;
  return (
    probe.auth_required === true &&
    probe.auth_hint === "oauth2" &&
    !asMeta?.registration_endpoint
  );
}

// Resolve a setup instruction when the user must supply credentials manually:
// auth required, NOT Dynamic Client Registration (no registration_endpoint),
// and NOT CIMD. Covers oauth2 (no DCR) and oauth2_client_credentials — both
// require the user to paste a client_id/secret, so a per-app guide helps.
export function shouldResolveInstruction(probe: MCPProbeResult): boolean {
  if (!probe.auth_required) return false;
  const asMeta = probe.as_metadata as AsMetadataShape | undefined;
  if (asMeta?.registration_endpoint) return false; // DCR available → no manual creds
  if (probe.auth_hint === "oauth2_cimd") return false; // CIMD → no manual creds
  return (
    probe.auth_hint === "oauth2" ||
    probe.auth_hint === "oauth2_client_credentials"
  );
}

// Future backend signal for design d05 ("Admin approval required") — tracked
// as gap G1 in docs/mcp-connector/checklist.md; renders once the field exists.
export function probeNeedsAdminApproval(probe: MCPProbeResult): boolean {
  return (
    (probe as unknown as Record<string, unknown>).requires_admin_approval ===
    true
  );
}

/**
 * Derive the persisted auth_type from the probe + the optional Client ID:
 *  - Direct mode               → "none", without an OAuth probe
 *  - Client ID given + CC hint → "oauth2_client_credentials"
 *  - Client ID given           → "oauth2" (secret optional; PKCE public ok)
 *  - else auth required        → honor a usable hint, else default CIMD
 *  - else                      → "none" (public; custom headers only)
 */
export function deriveAuthType(
  probe: MCPProbeResult | null,
  clientId: string,
  mode: McpConnectionMode = "auto",
): DerivedAuthType {
  if (mode === "direct") return "none";
  if (!probe) throw new Error("MCP probe is required for automatic authentication");
  if (clientId) {
    return probe.auth_hint === "oauth2_client_credentials"
      ? "oauth2_client_credentials"
      : "oauth2";
  }
  if (probe.auth_required) {
    return probe.auth_hint === "oauth2" || probe.auth_hint === "oauth2_cimd"
      ? probe.auth_hint
      : "oauth2_cimd";
  }
  return "none";
}

// The RFC 8414 / 9728 endpoints + advertised scopes the connect flow needs.
export interface McpAuthEndpoints {
  authzUrl: string;
  tokenUrl: string;
  regEndpoint: string;
  scopes: string[];
}

/**
 * Pull the OAuth endpoints (RFC 8414) + advertised scopes (RFC 9728) out of a
 * probe result. Shared by the Add modal and the Edit panel so the field
 * plumbing stays in one place.
 */
export function authEndpointsFromProbe(
  probe: MCPProbeResult | null,
): McpAuthEndpoints {
  const asMeta = probe?.as_metadata as AsMetadataShape | undefined;
  const prm = probe?.prm as PrmShape | undefined;
  return {
    authzUrl: asMeta?.authorization_endpoint ?? "",
    tokenUrl: asMeta?.token_endpoint ?? "",
    regEndpoint: asMeta?.registration_endpoint ?? "",
    scopes: prm?.scopes_supported ?? [],
  };
}

/**
 * Build the AuthConfig write body for a derived auth type — shared VERBATIM by
 * the Add modal and the Edit panel so the oauth2 / oauth2_cimd /
 * oauth2_client_credentials shapes can never drift. `clientId` / `clientSecret`
 * are the already-trimmed static credentials; empty values are omitted. (Cast
 * through `unknown` like the call sites did: `scope_level` + the *_cimd /
 * *_client_credentials blocks are wire fields the AuthConfig type doesn't model.)
 */
export function buildMcpAuthConfig(
  systemId: string,
  derived: DerivedAuthType,
  endpoints: McpAuthEndpoints,
  clientId: string,
  clientSecret: string,
): AuthConfig {
  const { authzUrl, tokenUrl, regEndpoint, scopes } = endpoints;
  const body: AuthConfig = {
    system_id: systemId,
    auth_type: derived,
    scope_level: "workspace",
  } as unknown as AuthConfig;
  if (derived === "oauth2") {
    body.oauth2 = {
      client_id: clientId,
      authorization_url: authzUrl,
      token_url: tokenUrl,
      ...(clientSecret ? { client_secret: clientSecret } : {}),
      ...(regEndpoint ? { registration_endpoint: regEndpoint } : {}),
      ...(scopes.length > 0 ? { scopes } : {}),
    };
  } else if (derived === "oauth2_cimd") {
    (body as unknown as Record<string, unknown>).oauth2_cimd = {
      authorization_url: authzUrl,
      token_url: tokenUrl,
      ...(scopes.length > 0 ? { scopes } : {}),
    };
  } else if (derived === "oauth2_client_credentials") {
    (body as unknown as Record<string, unknown>).oauth2_client_credentials = {
      client_id: clientId,
      client_secret: clientSecret,
      token_url: tokenUrl,
    };
  }
  return body;
}
